import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, CheckCircle, XCircle, Truck, Package, Calendar, MapPin, Printer } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useGRN, useUpdateGRNStatus } from '@/hooks/finance/useGRN';
import { GRNStatus } from '@/types/database/grn';
import { format } from 'date-fns';
import DocumentUpload from '@/components/finance/DocumentUpload';
import DocumentList from '@/components/finance/DocumentList';

const statusConfig: Record<GRNStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  draft: { label: 'Draft', variant: 'secondary' },
  received: { label: 'Received', variant: 'outline' },
  verified: { label: 'Verified', variant: 'default' },
  closed: { label: 'Closed', variant: 'secondary' },
};

const verificationStatusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending: { label: 'Pending Verification', variant: 'outline' },
  passed: { label: 'Passed', variant: 'default' },
  failed: { label: 'Failed', variant: 'destructive' },
};

const GRNDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { data: grn, isLoading, refetch } = useGRN(id || '');
  const updateStatus = useUpdateGRNStatus();

  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [discrepancyDialogOpen, setDiscrepancyDialogOpen] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');

  const canReceive = grn?.status === 'draft';
  const canVerify = grn?.status === 'received' && grn?.verification_status === 'pending';

  const handleReceive = async () => {
    if (!id) return;
    await updateStatus.mutateAsync({ id, status: 'received' });
    setReceiveDialogOpen(false);
  };

  const handleVerify = async () => {
    if (!id) return;
    await updateStatus.mutateAsync({ 
      id, 
      status: 'verified',
      verification_status: 'passed',
    });
    setVerifyDialogOpen(false);
  };

  const handleReportDiscrepancy = async () => {
    if (!id || !notes) return;
    await updateStatus.mutateAsync({ 
      id, 
      status: 'received',
      verification_status: 'failed',
      notes,
    });
    setDiscrepancyDialogOpen(false);
    setNotes('');
  };

  const handleDocumentUploaded = async () => {
    refetch();
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  if (!grn) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">GRN not found</h2>
          <Button className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </Layout>
    );
  }

  const currentStatusConfig = statusConfig[grn.status] || statusConfig.draft;
  const currentVerificationConfig = verificationStatusConfig[grn.verification_status || 'pending'];

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-foreground">{grn.grn_number}</h1>
                <Badge variant={currentStatusConfig.variant}>
                  {currentStatusConfig.label}
                </Badge>
                {grn.verification_status && (
                  <Badge variant={currentVerificationConfig.variant}>
                    {currentVerificationConfig.label}
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground">
                Delivery on {format(new Date(grn.delivery_date), 'MMM d, yyyy')}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/finance/accounting/grn/${id}/edit`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            {canReceive && (
              <Button onClick={() => setReceiveDialogOpen(true)}>
                <Package className="h-4 w-4 mr-2" />
                Mark Received
              </Button>
            )}
            {canVerify && (
              <>
                <Button variant="destructive" onClick={() => setDiscrepancyDialogOpen(true)}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Report Discrepancy
                </Button>
                <Button onClick={() => setVerifyDialogOpen(true)}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Verify
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Delivery Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Delivery Date</h4>
                  <p className="mt-1">{format(new Date(grn.delivery_date), 'MMM d, yyyy')}</p>
                </div>
                {grn.delivery_note_number && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground">Delivery Note #</h4>
                    <p className="mt-1">{grn.delivery_note_number}</p>
                  </div>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {grn.vehicle_number && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground">Vehicle Number</h4>
                    <p className="mt-1">{grn.vehicle_number}</p>
                  </div>
                )}
                {grn.driver_name && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground">Driver Name</h4>
                    <p className="mt-1">{grn.driver_name}</p>
                  </div>
                )}
              </div>

              {grn.warehouse_location && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Warehouse Location</h4>
                  <p className="mt-1">{grn.warehouse_location}</p>
                </div>
              )}

              <Separator />

              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Items Received</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3">Product</th>
                        <th className="text-center p-3">Unit</th>
                        <th className="text-right p-3">Ordered</th>
                        <th className="text-right p-3">Received</th>
                        <th className="text-right p-3">Variance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grn.items?.map((item) => {
                        const variance = item.received_quantity - item.ordered_quantity;
                        return (
                          <tr key={item.id} className="border-t">
                            <td className="p-3">
                              <p className="font-medium">{item.product_name}</p>
                              {item.quality_notes && (
                                <p className="text-xs text-muted-foreground">{item.quality_notes}</p>
                              )}
                            </td>
                            <td className="text-center p-3">{item.unit || 'kg'}</td>
                            <td className="text-right p-3">{item.ordered_quantity.toLocaleString()}</td>
                            <td className="text-right p-3">{item.received_quantity.toLocaleString()}</td>
                            <td className={`text-right p-3 ${variance !== 0 ? (variance > 0 ? 'text-green-600' : 'text-red-600') : ''}`}>
                              {variance > 0 ? '+' : ''}{variance.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {grn.notes && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground">Notes</h4>
                    <p className="mt-1">{grn.notes}</p>
                  </div>
                </>
              )}

              {grn.discrepancy_notes && (
                <>
                  <Separator />
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                    <h4 className="font-medium text-destructive">Discrepancy Notes</h4>
                    <p className="mt-1 text-sm">{grn.discrepancy_notes}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Created</p>
                    <p className="font-medium">
                      {grn.created_at ? format(new Date(grn.created_at), 'MMM d, yyyy') : 'N/A'}
                    </p>
                  </div>
                </div>
                {grn.received_at && (
                  <div className="flex items-center gap-3">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Received</p>
                      <p className="font-medium">
                        {format(new Date(grn.received_at), 'MMM d, yyyy HH:mm')}
                      </p>
                    </div>
                  </div>
                )}
                {grn.verified_at && (
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Verified</p>
                      <p className="font-medium">
                        {format(new Date(grn.verified_at), 'MMM d, yyyy HH:mm')}
                      </p>
                    </div>
                  </div>
                )}
                {grn.warehouse_location && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Location</p>
                      <p className="font-medium">{grn.warehouse_location}</p>
                    </div>
                  </div>
                )}
                <Separator />
                <div>
                  <p className="text-sm text-muted-foreground">Total Items</p>
                  <p className="text-2xl font-bold">{grn.items?.length || 0}</p>
                </div>
              </CardContent>
            </Card>

            {/* Supplier/Agent Info */}
            {(grn.supplier || grn.agent) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Supplier / Agent</CardTitle>
                </CardHeader>
                <CardContent>
                  {grn.supplier && (
                    <div>
                      <p className="text-sm text-muted-foreground">Supplier</p>
                      <p className="font-medium">{grn.supplier.name}</p>
                    </div>
                  )}
                  {grn.agent && (
                    <div className="mt-2">
                      <p className="text-sm text-muted-foreground">Agent</p>
                      <p className="font-medium">{grn.agent.name}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Documents Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Documents</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <DocumentUpload
                  bucketName="grn-documents"
                  entityId={grn.id}
                  entityType="grn"
                  documentTypes={[
                    { value: 'delivery_note', label: 'Delivery Note' },
                    { value: 'weight_slip', label: 'Weight Slip' },
                    { value: 'quality_certificate', label: 'Quality Certificate' },
                    { value: 'photo', label: 'Photo' },
                    { value: 'other', label: 'Other' },
                  ]}
                  onUploadComplete={handleDocumentUploaded}
                />
                {grn.documents && grn.documents.length > 0 && (
                  <DocumentList
                    documents={grn.documents.map(d => ({
                      id: d.id,
                      file_name: d.file_name,
                      file_path: d.file_path,
                      document_type: d.document_type,
                      created_at: d.created_at,
                    }))}
                    bucketName="grn-documents"
                    onDelete={handleDocumentUploaded}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Receive Dialog */}
      <Dialog open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Received</DialogTitle>
            <DialogDescription>
              Confirm that the goods have been physically received at the warehouse.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleReceive} disabled={updateStatus.isPending}>
              Confirm Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify Dialog */}
      <Dialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verify GRN</DialogTitle>
            <DialogDescription>
              Confirm that the received quantities match the documentation and quality is acceptable.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleVerify} disabled={updateStatus.isPending}>
              Verify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discrepancy Dialog */}
      <Dialog open={discrepancyDialogOpen} onOpenChange={setDiscrepancyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Discrepancy</DialogTitle>
            <DialogDescription>
              Document any issues with the received goods.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Discrepancy Details *</Label>
              <Textarea
                placeholder="Describe the discrepancy..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscrepancyDialogOpen(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleReportDiscrepancy} 
              disabled={!notes || updateStatus.isPending}
            >
              Report Discrepancy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default GRNDetailPage;
